import { Info as TabIconInfo, FileJson as TabIconFileJson, Send as TabIconSend, ShieldCheck as TabIconShieldCheck, ListChecks as TabIconListChecks, Globe as TabIconGlobe, ChartColumn as TabIconChartColumn, Webhook as TabIconWebhook, Paperclip as TabIconPaperclip, Logs as TabIconLogs } from "lucide-react";
import { useLocale } from "../../../hooks/useLocale";
import { msg } from "../../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDateTime, parseServerDate } from "src/utils/dateTime";
import Pre from "@ui/pre/Pre";
import { Box, HBox, VBox } from "@ui/box/Box";
import { Error } from "@ui/error/Error";
import Panel from "@ui/panel/Panel";
import Spacer from "@ui/spacer/Spacer";
import { TitledValue } from "@ui/table/TitledValue";
import { TabItem } from "@ui/tabs/Tabs";
import { ITheme } from "@ui/theme/GlobalTheme";
import { OverflowTabs } from "../OverflowTabs";
import { Fragment, useEffect, useRef, useState } from "react";
import { GD } from "src/GD";
import { GBox, HeadBlock } from "../GBox";
import { Table } from "@ui/table/Table";
import { Rows } from "@ui/table/Rows";
import { Typo } from "@ui/text/Typo";
import { IconFile2 } from "@ui/icons/IconFile2";
import styled, { useTheme } from "styled-components";
import { S_RAW_TEXT_POPUP } from "./RawTextPopup";
import { S_LIST_VALUES_POPUP } from "./ListValuesPopup";
import { S_WEBHOOK_DETAIL_POPUP } from "./WebhookDetailPopup";
import { DectaSessionDetails } from "./DectaSessionDetails";
import { Badge } from "@ui/badge/Badge";
import Signal from "badmfck-signal";

import { S_REFUND_POPUP } from "./RefundPopup";

import { DectaSessionLog } from "../SessionLog";
import { DectaFileActions, getDectaFilePath, isPreviewable, loadDectaFileBlob, openDectaFilePopup } from "src/utils/dectaFile";
import { ArrowRightLeft, CodeXml, Copy, List, X } from "lucide-react";
import { notify } from "src/tools/notify";
import { DectaPayloadPreview } from "./DectaPayloadPreview";

type IDectaSessionPopupProps = {};

export const S_DECTA_SESSION_POPUP = new Signal<{ sessionId: string } | null>("S_DECTA_SESSION_POPUP");

const SessionHeader = styled.header`
	display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
	flex: 0 0 auto; padding: 20px 24px 16px; background: #fff; border-bottom: 1px solid #edf0f5;
	font: 13px/1.5 "Euclid Circular B", sans-serif; color: #344054;
	.context { font-size: 11px; color: #667085; margin-bottom: 5px; }
	h2 { margin: 0; font-size: 21px; font-weight: 600; line-height: 1.3; color: #182230; }
	button { display: grid; place-items: center; flex-shrink: 0; width: 32px; height: 32px; border: 0; border-radius: 7px; background: transparent; color: #667085; cursor: pointer; }
	button:hover { background: #f5f7fa; }
`;

export const DectaSessionPopup = (_props: IDectaSessionPopupProps) => {
	useLocale();
	const requestData = S_DECTA_SESSION_POPUP.use();

	const [error, setError] = useState<IError>();
	const [busy, setBusy] = useState<boolean>(false);
	const [dectaSession, setDectaSession] = useState<IDectaSessionFull>();

	useEffect(() => {
		if (requestData) {
			fetchSessionData();
		}
	}, [requestData]);

	if (!requestData) return null;

	const fetchSessionData = async () => {
		setBusy(true);
		const res = await GD.REQ_API.request({
			method: "processing/session/" + requestData?.sessionId,
			data: {},
		});

		if (res.error) {
			setError(res.error);
		} else if (!res.data?.session) {
			// An id that is not a processing session (e.g. a payment session id) comes back without the Decta structure.
			setDectaSession(undefined);
			setError({ code: 0, message: msg("templateProcessingSessionWasNotFound_eafe9522", { v0: requestData?.sessionId }) });
		} else {
			setDectaSession(res.data);
			setError(undefined);
		}
		setBusy(false);
	};

	const closePopup = () => {
		setBusy(false);
		setError(undefined);
		setDectaSession(undefined);
		S_DECTA_SESSION_POPUP.invoke(null);
	};

	const requestSection = dectaSession?.payloads?.find((p: any) => p.type === "request");
	const paymentLogSection = dectaSession?.payloads?.find((p: any) => p.type === "payment");
	const threedsSection = dectaSession?.payloads?.find((p: any) => p.type === "threeDS");
	const browserSection = dectaSession?.payloads?.find(p => p.type === "browser");

	return (
		<Panel sx={{ width: "1200px", maxWidth: "calc(100vw - 32px)", minWidth: 0, height: "90dvh", maxHeight: "calc(100dvh - 24px)", padding: 0, borderRadius: "12px", overflow: "hidden", boxSizing: "border-box" }} disableScroll decorator="absolute" onClose={closePopup} busy={busy}>
			<SessionHeader>
				<div><div className="context">{msg("processingDecta")}</div><h2>{msg("dectaSessionData")}</h2></div>
				<button type="button" onClick={closePopup} aria-label={msg("close")}><X size={18} /></button>
			</SessionHeader>
			<Error error={error} />
			<OverflowTabs key={requestData.sessionId}>
				<TabItem title={msg("details_14fdc0f6")} icon={<TabIconInfo size={16} />}>
					<SessionDetails session={dectaSession?.session} relatedPayments={dectaSession?.relatedPayments} />
				</TabItem>
				<TabItem title={msg("raw_ac0562bb")} icon={<TabIconFileJson size={16} />}>
					<Pre data={dectaSession} wrap />
				</TabItem>

				{requestSection ? (
					<TabItem title={msg("request_041569db")} icon={<TabIconSend size={16} />}>
						<Pre data={requestSection.data} wrap />
					</TabItem>
				) : null}

				{threedsSection ? (
					<TabItem title={"3DS"} icon={<TabIconShieldCheck size={16} />}>
						<ThreeDS key={requestData.sessionId} data={threedsSection?.data as IDectaSession3ds} />
					</TabItem>
				) : null}

				{paymentLogSection ? (
					<TabItem title={msg("paymentLog")} icon={<TabIconListChecks size={16} />}>
						<PaymentLog key={requestData.sessionId} data={paymentLogSection?.data as IDectaSessionPayment} />
					</TabItem>
				) : null}

				<TabItem title={msg("browser")} icon={<TabIconGlobe size={16} />}>
					<BrowserDetails data={browserSection?.data as IDectaSessionBrowser | undefined} />
				</TabItem>

				<TabItem title={msg("reports_00a0183c")} icon={<TabIconChartColumn size={16} />}>
					<Reports data={dectaSession?.reports} />
				</TabItem>

				<TabItem title={msg("webhook_47e276fc")} icon={<TabIconWebhook size={16} />}>
					<Webhooks data={dectaSession?.webhooks} />
				</TabItem>

				<TabItem title={msg("files_faff10f7")} icon={<TabIconPaperclip size={16} />}>
					<Files data={dectaSession?.files} />
				</TabItem>

				<TabItem title={msg("log")} icon={<TabIconLogs size={16} />}>
					<DectaSessionLog data={dectaSession} />
				</TabItem>
			</OverflowTabs>
		</Panel>
	);
};

const hasBrowserValue = (value: unknown): boolean => {
	if (value == null || value === "") return false;
	if (typeof value === "object") return Object.values(value).some(hasBrowserValue);
	return true;
};

const BrowserDetails = ({ data }: { data?: IDectaSessionBrowser | null }) => {
	useLocale();
	if (!hasBrowserValue(data)) {
		return (
			<VBox center gap="10px" sx={{ minHeight: "260px", color: "textSecondary" }}>
				<Typo variant="headMedium">{msg("noBrowserData")}</Typo>
				<Typo variant="medium">{msg("browserInformationWasNotCollectedForThisSession")}</Typo>
			</VBox>
		);
	}

	const { info, headers, ...other } = data ?? {};
	const sections = [
		{ title: msg("browserInfo"), values: info },
		{ title: msg("httpHeaders"), values: headers },
		{ title: msg("additionalData"), values: other },
	];

	return (
		<VBox gap="20px" sx={{ minWidth: 0 }}>
			{sections.map(({ title, values }) => {
				if (!hasBrowserValue(values)) return null;
				return (
					<VBox key={title} gap="10px" sx={{ minWidth: 0 }}>
						<HeadBlock>{title}</HeadBlock>
						<GBox>
							{Object.entries(values ?? {}).map(([key, value]) => (
								<TitledValue
									key={key}
									variant="modern"
									title={key}
									value={
										<Box sx={{ minWidth: 0, overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}>
											{value == null || value === "" ? "—" : typeof value === "object" ? JSON.stringify(value, null, 2) : String(value)}
										</Box>
									}
								/>
							))}
						</GBox>
					</VBox>
				);
			})}
		</VBox>
	);
};

const StepsClip = ({ steps, compact = false }: { steps: { step: number; name?: string; request: object; xml: string; response: object }[]; compact?: boolean }) => {
	useLocale();
	return (
		<>
			{steps.map((step, index) => (
				<Fragment key={index}>
					{!compact && <Spacer size="30px" />}

					{!compact && <HeadBlock>{msg("step")}{step.step} {step.name ?? ""}</HeadBlock>}

					<VBox sx={{ paddingLeft: compact ? "0" : "40px" }}>
						<Spacer size="10px" />
						<Typo variant="medium" sx={{ fontWeight: 600 }}>{msg("request")}</Typo>
						<Spacer size="10px" />
						<DectaPayloadPreview data={step.request} />
						<Spacer size="10px" />
						<Typo variant="medium" sx={{ fontWeight: 600 }}>{msg("xml")}</Typo>
						<Spacer size="10px" />
						<Pre data={step.xml} wrap />
						<Spacer size="10px" />
						<Typo variant="medium" sx={{ fontWeight: 600 }}>{msg("response")}</Typo>
						<Spacer size="10px" />
						<DectaPayloadPreview data={step.response} />
					</VBox>
				</Fragment>
			))}
		</>
	);
};

const PaymentLog = ({ data }: { data: IDectaSessionPayment }) => {
	useLocale();
	const { type, steps = [] } = data;
	const [selection, setSelection] = useState(0);
	const contentRef = useRef<HTMLDivElement>(null);
	const sectionRefs = useRef<(HTMLElement | null)[]>([]);
	const scrollToStep = (index: number) => {
		const container = contentRef.current;
		const section = sectionRefs.current[index];
		if (!container || !section) return;
		setSelection(index);
		container.scrollTo({
			top: container.scrollTop + section.getBoundingClientRect().top - container.getBoundingClientRect().top - 16,
			behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
		});
	};

	return (
		<SectionsSplit className="decta-sections-split">
			<nav className="section-menu" aria-label={msg("paymentLog")}>
				<div className="steps-summary">{msg("steps")} · {steps.length}</div>
				{steps.map((step, index) => {
					const title = msg("templateStep_5e406ade", { v0: step.step });
					const name = "name" in step ? String(step.name ?? "") : "";
					return (
						<button key={index} type="button" aria-current={selection === index ? "location" : undefined}
							title={name ? `${title}: ${name}` : title} onClick={() => scrollToStep(index)}>
							<ArrowRightLeft size={22} aria-hidden="true" style={{ flexShrink: 0 }} />
							<span className="label"><span className="title">{title}</span>{name && <span className="name">{name}</span>}</span>
						</button>
					);
				})}
			</nav>
			<div ref={contentRef} className="section-content" tabIndex={0} role="region" aria-label={msg("paymentLog")}>
				<Typo variant="medium" sx={{ fontWeight: 600, marginBottom: "20px" }}>{msg("type_46f8246f")}{type}</Typo>
				{steps.map((step, index) => (
					<section key={index} ref={element => { sectionRefs.current[index] = element; }}>
						<h3>{msg("templateStep_5e406ade", { v0: step.step })}{"name" in step && step.name ? ` · ${step.name}` : ""}</h3>
						<div className="section-card"><StepsClip steps={[step]} compact /></div>
					</section>
				))}
			</div>
		</SectionsSplit>
	);
};

const SectionsSplit = styled.div`
	display: flex; flex: 1 1 0; min-height: 0; min-width: 0; overflow: hidden;
	font: 13px/1.5 "Euclid Circular B", sans-serif; color: #344054;
	.section-menu {
		display: flex; flex-direction: column; flex: 0 0 185px; width: 185px;
		min-height: 0; overflow-y: auto; overflow-x: hidden; overscroll-behavior: contain;
		padding-top: 16px; border-right: 1px solid #edf0f5; background: #fff;
	}
	.section-menu button {
		display: flex; align-items: center; gap: 10px; flex-shrink: 0; min-width: 0;
		padding: 20px 16px; text-align: left; border: 0; border-left: 5px solid transparent;
		border-bottom: 1px solid rgba(0,0,0,.1); background: transparent;
		color: #667085; cursor: pointer; font: inherit;
	}
	.section-menu button[aria-current] { border-left-color: rgb(0,145,255); color: rgb(0,145,255); font-weight: 600; }
	.section-menu button:hover { background: #f5f7fa; }
	.section-menu button:focus-visible { outline: 2px solid #528bff; outline-offset: -3px; }
	.section-menu .label { min-width: 0; }
	.section-menu .title, .section-menu .name { display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
	.section-menu .name { margin-top: 3px; font-size: 11px; font-weight: 400; color: #98a2b3; }
	.steps-summary { flex-shrink: 0; padding: 0 16px 12px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: #667085; }
	.section-content {
		flex: 1 1 0; min-width: 0; min-height: 0; overflow: auto; overscroll-behavior: contain;
		background: #f5f7fa; padding: 16px;
	}
	section { margin-bottom: 24px; min-width: 0; }
	section:last-child { margin-bottom: 0; }
	h3 { margin: 0 0 10px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: #667085; overflow-wrap: anywhere; }
	.section-card { min-width: 0; padding: 18px 20px; border: 1px solid #d0d5dd; border-radius: 12px; background: #fff; box-shadow: 0 1px 2px rgba(16,24,40,.05); }
	.details-list { margin: 0; }
	.detail-row { display: grid; grid-template-columns: minmax(130px, 1fr) minmax(0, 2fr); gap: 16px; padding: 12px 0; border-bottom: 1px solid #edf0f5; }
	.detail-row:first-child { padding-top: 0; }
	.detail-row:last-child { padding-bottom: 0; border-bottom: 0; }
	dt { color: #667085; overflow-wrap: anywhere; }
	dd { margin: 0; color: #344054; overflow-wrap: anywhere; white-space: pre-wrap; }
	@media(max-width: 768px) {
		flex-direction: column;
		.section-menu { flex: 0 0 auto; flex-direction: row; width: auto; padding-top: 0; overflow-x: auto; border-right: 0; border-bottom: 1px solid #edf0f5; }
		.section-menu button { padding: 12px; border-left: 0; border-bottom: 3px solid transparent; }
		.section-menu button[aria-current] { border-bottom-color: rgb(0,145,255); }
		.steps-summary { align-self: center; padding: 12px; white-space: nowrap; }
		.section-content { padding: 16px; }
		.section-card { padding: 16px; }
		.detail-row { grid-template-columns: minmax(0, 1fr); gap: 4px; }
	}
`;

const ThreeDS = ({ data }: { data: IDectaSession3ds }) => {
	useLocale();
	const { iframeInject, steps, threedsInject, ...values } = data;
	const [selection, setSelection] = useState("details");
	const contentRef = useRef<HTMLDivElement>(null);
	const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
	const sections = [
		{ id: "details", title: msg("details"), name: "", icon: List, content: <TableClip values={values} /> },
		{ id: "iframe", title: msg("iframeInject"), name: "", icon: CodeXml, content: <DectaPayloadPreview data={iframeInject} /> },
		{ id: "3ds", title: msg("text3dsInject"), name: "", icon: CodeXml, content: <DectaPayloadPreview data={threedsInject} /> },
		...(steps ?? []).map((step, index) => ({
			id: `step-${index}`,
			title: msg("templateStep_5e406ade", { v0: step.step }),
			name: "name" in step ? step.name : "",
			icon: ArrowRightLeft,
			content: <StepsClip steps={[step]} compact />,
		})),
	];
	const scrollToSection = (id: string) => {
		const container = contentRef.current;
		const section = sectionRefs.current[id];
		if (!container || !section) return;
		setSelection(id);
		container.scrollTo({
			top: container.scrollTop + section.getBoundingClientRect().top - container.getBoundingClientRect().top - 20,
			behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
		});
	};

	return (
		<SectionsSplit className="decta-sections-split">
			<nav
				aria-label={msg("text3dsSections")}
				className="section-menu">
				{sections.map(section => {
					const active = selection === section.id;
					const Icon = section.icon;
					return (
						<button
							key={section.id}
							type="button"
							aria-current={active ? "location" : undefined}
							title={section.name ? `${section.title}: ${section.name}` : section.title}
							onClick={() => scrollToSection(section.id)}>
							<Icon size={22} aria-hidden="true" style={{ flexShrink: 0 }} />
							<span className="label">
								<span className="title">{section.title}</span>
								{section.name && <span className="name">{section.name}</span>}
							</span>
						</button>
					);
				})}
			</nav>
			<div
				ref={contentRef}
				className="section-content" tabIndex={0} role="region" aria-label={msg("text3dsContent")}>
				{sections.map(section => (
					<section
						key={section.id}
						aria-label={section.name ? `${section.title}: ${section.name}` : section.title}
						ref={element => { sectionRefs.current[section.id] = element; }}>
						<h3>{section.title}{section.name ? ` · ${section.name}` : ""}</h3>
						<div className="section-card">{section.content}</div>
					</section>
				))}
			</div>
		</SectionsSplit>
	);
};

const TableClip = ({ values }: { values: Record<string, any> }) => {
	useLocale();
	return (
		<dl className="details-list">
			{values &&
				Object.entries(values).map(([key, val]) => (
					<div className="detail-row" key={key}>
						<dt>{key}</dt>
						<dd>{val == null || val === "" ? "—" : typeof val === "object" ? JSON.stringify(val, null, 2) : String(val)}</dd>
					</div>
				))}
		</dl>
	);
};

const getSessionFileExtension = (fileName: string) => {
	const lower = fileName.toLowerCase();
	if (lower.endsWith(".txt-simple")) return "txt-simple";
	return lower.includes(".") ? lower.split(".").pop() ?? "" : "";
};

const getFileStatusColor = (status: string) => {
	switch (status?.toLowerCase()) {
		case "completed":
		case "uploaded":
		case "processed":
			return "#22a06b";
		case "failed":
		case "error":
			return "#dc3545";
		case "pending":
		case "processing":
			return "#d97706";
		default:
			return "#8b95a5";
	}
};

const CopyableFilePath = ({ path }: { path: string }) => { useLocale(); return ((
	<HBox
		title={msg("copyFilePath")}
		onClick={event => {
			event?.stopPropagation();
			navigator.clipboard.writeText(path);
			notify.copied();
		}}
		sx={{
			alignItems: "center",
			gap: "5px",
			minWidth: 0,
			width: "max-content",
			maxWidth: "100%",
			cursor: "copy",
			borderRadius: "4px",
			padding: "2px 5px",
			margin: "-2px -5px",
			transition: "background-color .15s",
			"&:hover": { backgroundColor: "rgba(0,0,0,.06)" },
		}}>
		<Typo variant="caption" color="textSecondary" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
			{path}
		</Typo>
		<Copy size={13} opacity={0.55} />
	</HBox>
)); };

const Files = ({ data }: { data?: IDectaSessionFile[] }) => {
	useLocale();
	const theme = useTheme() as ITheme;
	const files = [...(data ?? [])].sort((a, b) => {
		const aTime = (parseServerDate(a.file_created_date || a.created_at)?.getTime() ?? 0);
		const bTime = (parseServerDate(b.file_created_date || b.created_at)?.getTime() ?? 0);
		return (Number.isNaN(bTime) ? 0 : bTime) - (Number.isNaN(aTime) ? 0 : aTime);
	});

	if (!files.length) {
		return (
			<VBox
				center
				gap="10px"
				sx={{ minHeight: "260px", border: "1px dashed rgba(0,0,0,.15)", borderRadius: "6px", color: "textSecondary" }}>
				<IconFile2 size={42} />
				<Typo variant="headMedium">{msg("noFiles")}</Typo>
				<Typo variant="medium">{msg("thereAreNoFilesAttachedToThisSession")}</Typo>
			</VBox>
		);
	}

	return (
		<Rows<IDectaSessionFile>
			order={["file_name", "status", "file_created_date"]}
			gridTemplateColumns="minmax(280px, 1fr) max-content max-content"
			data={files}
			searchable
			onClick={(row, _cell) => {
				const fullPath = getDectaFilePath(row.file_path, row.file_name);
				openDectaFilePopup({
					name: row.file_name,
					path: fullPath,
					previewable: isPreviewable(row.file_name),
					load: row.file_path
						? () => loadDectaFileBlob(fullPath)
						: async () => new Blob([row.data ?? ""], { type: "text/plain;charset=utf-8" }),
				});
			}}
			cellFactory={(row, cell, _index, _key, name, sx, _expanded, _rowId) => {
				sx && (sx.backgroundColor = theme.colors.backgroundPrimary);
				if (name === "file_name") {
					const extension = getSessionFileExtension(row.file_name);
					const fullPath = getDectaFilePath(row.file_path, row.file_name);
					return {
						content: (
							<HBox gap="12px" sx={{ alignItems: "center" }}>
								<IconFile2 size={32} />
								<VBox gap="5px" sx={{ minWidth: 0 }}>
									<HBox gap="8px" sx={{ alignItems: "center", minWidth: 0 }}>
										<Typo variant="medium" sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
											{row.file_name}
										</Typo>
										{extension && <Badge variant="outline" label={extension.toUpperCase()} />}
										{row.prefix && <Badge variant="outline" label={row.prefix} />}
									</HBox>
									{row.file_path && <CopyableFilePath path={fullPath} />}
								</VBox>
							</HBox>
						),
						sx: sx,
					};
				}

				if (name === "status") {
					return {
						content: (
							<VBox gap="5px" sx={{ alignItems: "flex-start" }}>
								<Badge label={row.status || msg("unknown_b23a6a84")} color={getFileStatusColor(row.status)} />
								{row.fail_reason_id != null && (
									<Typo variant="caption" color="textSecondary">{msg("failure")}{row.fail_reason_id}
									</Typo>
								)}
							</VBox>
						),
						sx,
					};
				}

				if (name === "file_created_date") {
					const dateLabel = formatDisplayDateTime(row.file_created_date || row.created_at, { separator: " - " });
					return {
						content: (
							<HBox gap="10px" sx={{ alignItems: "center", justifyContent: "flex-end" }}>
								<Typo variant="medium" color="textSecondary" sx={{ whiteSpace: "nowrap" }}>
									<DateTimeTooltip value={row.file_created_date || row.created_at}>{dateLabel}</DateTimeTooltip>
								</Typo>
								{row.file_path && <DectaFileActions filePath={row.file_path} fileName={row.file_name} />}
							</HBox>
						),
						sx: sx,
					};
				}

				// if (name === "merchant_id") {
				// 	return {
				// 		content: (
				// 			<VBox>
				// 				<Typo variant="caption" color="textSecondary">
				// 					Merchant
				// 				</Typo>
				// 				<Typo variant="medium">{cell}</Typo>
				// 			</VBox>
				// 		),
				// 	};
				// }

				return {
					content: cell,
					sx: sx,
				};
			}}
		/>
	);
};

const Reports = ({ data }: { data?: IDectaSessionReport[] }) => {
	useLocale();
	const theme = useTheme() as ITheme;

	return (
		<Rows<IDectaSessionReport>
			order={["payment_id", "type", "created_at"]}
			gridTemplateColumns="auto max-content max-content"
			data={data ?? []}
			onClick={(row, _cell) => {
				S_LIST_VALUES_POPUP.invoke({
					title: msg("reportForPayment") + row.payment_id,
					value: row.data,
				});
			}}
			cellFactory={(row, cell, _index, _key, name, sx, _expanded, _rowId) => {
				sx && (sx.backgroundColor = theme.colors.backgroundPrimary);
				// if (name === "file_name") {
				// 	return {
				// 		content: (
				// 			<HBox gap="12px" sx={{ alignItems: "center" }}>
				// 				<IconFile2 size={32} />
				// 				<VBox>
				// 					<Typo variant="medium">{row.file_name}</Typo>
				// 					<Typo variant="caption" color="textSecondary">
				// 						{row.file_path}
				// 					</Typo>
				// 				</VBox>
				// 			</HBox>
				// 		),
				// 		sx: sx,
				// 	};
				// }

				if (name === "payment_id") {
					return {
						content: (
							<VBox>
								<Typo variant="caption" color="textSecondary">{msg("paymentId_d9c9afd8")}</Typo>
								<Typo variant="medium">{row.payment_id}</Typo>
							</VBox>
						),
						sx: sx,
					};
				}

				if (name === "type") {
					return {
						content: (
							<VBox>
								<Typo variant="caption" color="textSecondary">{msg("type")}</Typo>
								<Typo variant="medium">{row.type}</Typo>
							</VBox>
						),
						sx: sx,
					};
				}

				if (name === "created_at") {
					return {
						content: (
							<Typo variant="medium" color="textSecondary">
								<DateTimeTooltip value={row.created_at}>{formatDisplayDateTime(row.created_at, { separator: " - " })}</DateTimeTooltip>
							</Typo>
						),
						sx: sx,
					};
				}

				return {
					content: cell,
					sx: sx,
				};
			}}
		/>
	);
};

const Webhooks = ({ data }: { data?: IDectaSessionPayloadWebhook[] }) => {
	useLocale();
	return (
		<>
			<Table<IDectaSessionPayloadWebhook>
				//
				order={["http_status", "type", "created_at", "url", "message", "attempts"]}
				cellFactory={(row, cell, _index, _key, name) => ({
					content: name === "created_at"
						? <DateTimeTooltip value={row.created_at}>{formatDisplayDateTime(row.created_at)}</DateTimeTooltip>
						: cell,
				})}
				data={data ?? []}
				onRowClick={row => {
					S_WEBHOOK_DETAIL_POPUP.invoke(row);
				}}
			/>
		</>
	);
};

const SessionDetails = ({ session, relatedPayments }: { session?: IDectaSession; relatedPayments?: IDectaRelatedPayment[] }) => {
	useLocale();
	if (!session) return <div role="status">{msg("noSessionDataAvailable")}</div>;
	return <DectaSessionDetails session={session} relatedPayments={relatedPayments}
		onInspect={async () => {
			const res = await GD.REQ_API.request({ method: "processing/session/" + session.ssid + "/decta", data: {} });
			let raw: any = res.error ?? res.data;
			while (typeof raw === "string") {
				try { raw = JSON.parse(raw); } catch { break; }
			}
			S_RAW_TEXT_POPUP.invoke({ title: msg("dectaSession") + session.decta_payment_id, value: JSON.stringify(raw, null, 2) });
		}}
		onRefund={() => S_REFUND_POPUP.invoke({ amount: session.amount, sessionId: session.external_payment_id, method: "gate/refund", currency: session.currency })}
		onRelated={sessionId => {
			S_DECTA_SESSION_POPUP.invoke(null);
			setTimeout(() => S_DECTA_SESSION_POPUP.invoke({ sessionId }), 300);
		}}
	/>;
};
